import { useState, useEffect, useRef, useCallback } from "react";
import api, { downloadFile } from "../../../BrandOnBoarding/api";

const POLL_INTERVAL = 15000; // 15 seconds
const MAX_POLL_ATTEMPTS = 150; // 15 minutes
const MONITORING_TYPE = "brand_monitoring_html";

/** Map backend status values to the frontend's status vocabulary. */
function normalizeStatus(status) {
  if (status === "running" || status === "queued") return "in-progress";
  if (status === "success") return "completed";
  return status; // "pending", "completed", "failed", "cancelled" pass through
}

/**
 * Fetches monitoring report job history from backend.
 * Scoped by userId + type=brand_monitoring_html.
 */
async function fetchJobsFromAPI(userId) {
  try {
    const response = await api.get("/llm-reporting/html-history", {
      params: { userId, types: MONITORING_TYPE },
    });
    if (response.status === 200 && Array.isArray(response.data)) {
      return response.data
        .filter((job) => job.status !== "cancelled")
        .map((job) => ({
          jobId: job.id,
          status: normalizeStatus(job.status),
          selections: job.selections || {},
          createdAt: job.start_date || new Date().toISOString(),
          transactionId: job.transaction_id ? String(job.transaction_id) : null,
        }));
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * useMonitoringHTMLReportHistory
 *
 * Manages the list of monitoring HTML report jobs.
 * Mirrors useHTMLReportHistory but targets the monitoring endpoint.
 */
export default function useMonitoringHTMLReportHistory({ userId, customerId, selectedOptionShow }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const mountedRef = useRef(true);

  // ── polling ───────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      pollAttemptsRef.current = 0;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;

    pollTimerRef.current = setInterval(async () => {
      if (!mountedRef.current) { stopPolling(); return; }

      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling();
        setJobs((prev) =>
          prev.map((j) =>
            j.status === "in-progress" || j.status === "pending"
              ? { ...j, status: "failed" }
              : j
          )
        );
        return;
      }

      const updated = await fetchJobsFromAPI(userId);
      if (!updated || !mountedRef.current) return;

      setJobs(updated);

      const hasRunning = updated.some(
        (j) => j.status === "in-progress" || j.status === "pending"
      );
      if (!hasRunning) stopPolling();
    }, POLL_INTERVAL);
  }, [userId, stopPolling]);

  // ── load history ──────────────────────────────────────────────────────────

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    const resolved = (await fetchJobsFromAPI(userId)) ?? [];

    if (mountedRef.current) {
      setJobs(resolved);
      const hasRunning = resolved.some(
        (j) => j.status === "in-progress" || j.status === "pending"
      );
      if (hasRunning) startPolling();
    }
    setLoading(false);
  }, [userId, startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    loadHistory();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [loadHistory, stopPolling]);

  // ── public API ────────────────────────────────────────────────────────────

  /**
   * Start a new monitoring HTML report generation job.
   * Picks endpoint based on selections.reportKind:
   *   'baseline' → generateBaselineMonitoringHTMLReport
   *   'change'   → generateChangeMonitoringHTMLReport
   * Returns { jobId } from the backend.
   */
  const generateReport = useCallback(
    async (selections) => {
      const type = selectedOptionShow === "Product" ? "product" : "brand";
      const endpoint =
        selections.reportKind === "change"
          ? "/llm-reporting/generateChangeMonitoringHTMLReport"
          : "/llm-reporting/generateBaselineMonitoringHTMLReport";

      const res = await api.post(endpoint, {
        userId,
        customerId,
        type,
        selections,
      });
      const { jobId } = res.data;
      if (!jobId) throw new Error("No job ID received from server");
      return jobId;
    },
    [userId, customerId, selectedOptionShow]
  );

  /**
   * Optimistically add a new in-progress job and kick off polling.
   */
  const addJob = useCallback(
    (jobId, selections) => {
      const newJob = {
        jobId,
        status: "in-progress",
        selections,
        createdAt: new Date().toISOString(),
        transactionId: null,
      };
      setJobs((prev) => [newJob, ...prev]);
      startPolling();
    },
    [startPolling]
  );

  /**
   * Check if a completed job with same selections already exists.
   */
  const findCompletedJob = useCallback(
    (selections) => {
      return (
        jobs.find(
          (j) =>
            j.status === "completed" &&
            j.selections?.category === selections?.category &&
            JSON.stringify((j.selections?.llms || []).slice().sort()) ===
            JSON.stringify((selections?.llms || []).slice().sort()) &&
            JSON.stringify((j.selections?.markets || []).slice().sort()) ===
            JSON.stringify((selections?.markets || []).slice().sort()) &&
            JSON.stringify((j.selections?.persona || []).slice().sort()) ===
            JSON.stringify((selections?.persona || []).slice().sort()) &&
            JSON.stringify((j.selections?.geographies || []).slice().sort()) ===
            JSON.stringify((selections?.geographies || []).slice().sort()) &&
            j.selections?.startDate === selections?.startDate &&
            j.selections?.endDate === selections?.endDate
        ) || null
      );
    },
    [jobs]
  );

  /**
   * Download a completed report by jobId.
   * `nameInfo` = { reportKind, reportType, brandName } — mirrors the labeling logic in
   * MonitoringHTMLReportHistoryItem's ReportTypeChip so the downloaded filename matches
   * what the history table shows (Flash Change Report / Change Report / Baseline Report).
   */
  const downloadReport = useCallback(async (jobId, nameInfo = {}) => {
    const { reportKind, reportType, brandName } = nameInfo;
    const isFlash = reportType === "flash";
    const isChange =
      typeof reportKind === "string" && reportKind.toLowerCase().includes("change");
    const typeLabel = isFlash
      ? "Flash Change Report"
      : isChange
      ? "Change Report"
      : "Baseline Report";
    const brand = String(brandName || "").trim();
    const filename = `Monitoring ${typeLabel} - ${brand || "Report"}.html`;

    await downloadFile(`/llm-reporting/download/${jobId}`, filename);
  }, []);

  /**
   * View HTML report in a new browser tab.
   */
  const viewReport = useCallback(async (jobId) => {
    const response = await api.get(`/llm-reporting/download/${jobId}`, {
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      throw new Error("popup_blocked");
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, []);

  /**
   * Cancel or remove a job by jobId.
   */
  const cancelJob = useCallback(async (jobId) => {
    await api.get(`/llm-reporting/cancel/${jobId}`);
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  return {
    jobs,
    loading,
    generateReport,
    addJob,
    findCompletedJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory: loadHistory,
  };
}
